import React, { useEffect, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Link as MuiLink,
  Stack,
  Typography,
} from '@mui/material';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';

import { loadStripe } from '@stripe/stripe-js';
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js';

import { toast } from '@src/components/core/toaster';

// Shared "Add bank (ACH)" dialog used on both the staff billing page and
// the client portal. Uses Stripe's PaymentElement (not CardElement) so we
// get the full Financial Connections flow: client logs into their bank,
// picks an account, and we get back a reusable us_bank_account payment
// method — no microdeposits, no 3-day wait when the bank supports it.
// Falls back to microdeposit verification if instant isn't available.

function AddBankForm({ onSuccess, onCancel, settlementCopy }) {
  const stripe = useStripe();
  const elements = useElements();
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [microdepositUrl, setMicrodepositUrl] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements) return;
    setSaving(true);
    setError(null);

    const submitResult = await elements.submit();
    if (submitResult.error) {
      setError(submitResult.error.message);
      setSaving(false);
      return;
    }

    // confirmSetup hands control to Stripe's Financial Connections sheet
    // for bank login + account selection. On supported banks we get an
    // instant verified PM back; otherwise Stripe returns a hosted URL
    // for microdeposit verification (1-2 business days).
    const result = await stripe.confirmSetup({
      elements,
      confirmParams: {
        return_url: window.location.href,
      },
      redirect: 'if_required',
    });

    if (result.error) {
      setError(result.error.message);
      setSaving(false);
      return;
    }

    const setupIntent = result.setupIntent;
    const status = setupIntent?.status;

    if (status === 'succeeded') {
      toast.success('Bank account connected.');
      onSuccess(setupIntent.payment_method);
      return;
    }
    if (status === 'requires_action') {
      // Microdeposit fallback — Stripe returns a hosted page where the
      // client confirms two small test deposits over the next 1-2 days.
      const hostedUrl =
        setupIntent?.next_action?.verify_with_microdeposits
          ?.hosted_verification_url;
      if (hostedUrl) {
        setMicrodepositUrl(hostedUrl);
        // Surface the pending PM + URL to the parent so it can call
        // register-pending-verification immediately (without waiting
        // for the setup_intent.requires_action webhook).
        onSuccess(setupIntent.payment_method, {
          pendingVerification: true,
          setupIntentId: setupIntent.id,
          verificationUrl: hostedUrl,
        });
        return;
      }
    }
    setError('Setup did not complete. Please try again.');
    setSaving(false);
  };

  if (microdepositUrl) {
    return (
      <>
        <DialogContent>
          <Alert severity="info" icon={<AccountBalanceIcon />}>
            <Typography variant="body2" sx={{ mb: 1 }}>
              <strong>Almost done — verify your bank in 1–2 business days.</strong>
            </Typography>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Stripe will send two small test deposits to your account. Once
              they arrive, confirm the amounts at the link below to activate
              ACH payments.
            </Typography>
            <MuiLink href={microdepositUrl} target="_blank" rel="noopener noreferrer">
              Verify deposits →
            </MuiLink>
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCancel} variant="contained">
            Done
          </Button>
        </DialogActions>
      </>
    );
  }

  return (
    <form onSubmit={handleSubmit}>
      <DialogContent>
        <DialogContentText sx={{ mb: 2 }}>
          Connect a US bank account via Stripe. You'll log into your bank in
          a secure popup — we never see your login or account number.
          {' '}
          <strong>
            Most major banks verify instantly. Smaller banks and credit
            unions use small test deposits, which adds 1–2 business days.
          </strong>
          {settlementCopy ? ` ${settlementCopy}` : ''}
        </DialogContentText>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 1,
            p: 2,
          }}
        >
          <PaymentElement
            options={{
              wallets: { applePay: 'never', googlePay: 'never' },
            }}
          />
        </Box>
        {error && (
          <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
            {error}
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel} disabled={saving}>
          Cancel
        </Button>
        <Button type="submit" variant="contained" disabled={!stripe || saving}>
          {saving ? 'Connecting…' : 'Connect bank'}
        </Button>
      </DialogActions>
    </form>
  );
}

export default function AddBankDialog({
  open,
  onClose,
  profileId,
  createSetupIntent,
  // Optional — when supplied, called immediately after Stripe returns
  // a microdeposit-pending SetupIntent so the UI's "Pending verification"
  // chip shows up without waiting for the Stripe webhook to fire.
  registerPendingVerification,
  onAttached,
  // Copy nudge — staff and client pages each pass their own wording.
  settlementCopy = (
    'Heads up: ACH charges take 3–5 business days to clear, so the first ' +
    'few charges may overlap. After that it settles into a normal rhythm.'
  ),
}) {
  const [intent, setIntent] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [loadError, setLoadError] = useState(null);

  useEffect(() => {
    if (!open || !profileId) return undefined;
    let cancelled = false;
    setIntent(null);
    setLoadError(null);
    createSetupIntent(profileId, 'us_bank_account')
      .then((resp) => {
        if (cancelled) return;
        const data = resp?.data;
        if (!data?.client_secret || !data?.publishable_key) {
          setLoadError(
            'Unable to start bank setup. Please try again or contact support.',
          );
          return;
        }
        setIntent(data);
        setStripePromise((prev) => prev || loadStripe(data.publishable_key));
      })
      .catch((err) => {
        if (cancelled) return;
        setLoadError(
          err?.response?.data?.detail || err?.message || 'Failed to start setup.',
        );
      });
    return () => {
      cancelled = true;
    };
  }, [open, profileId, createSetupIntent]);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Connect a bank account</DialogTitle>
      {loadError ? (
        <>
          <DialogContent>
            <Alert severity="error">{loadError}</Alert>
          </DialogContent>
          <DialogActions>
            <Button onClick={onClose}>Close</Button>
          </DialogActions>
        </>
      ) : !intent || !stripePromise ? (
        <DialogContent>
          <Stack direction="row" alignItems="center" spacing={1}>
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Preparing secure connection…
            </Typography>
          </Stack>
        </DialogContent>
      ) : (
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret: intent.client_secret,
            // PaymentElement defaults — keeps ACH-only and hides the
            // accordion-style payment-method tabs we don't need here.
            appearance: { theme: 'stripe' },
          }}
        >
          <AddBankForm
            onSuccess={async (newPmId, opts = {}) => {
              if (opts.pendingVerification) {
                // Microdeposit branch — record the pending state on the
                // backend so the UI doesn't briefly show the bank as
                // "verified" while waiting for the webhook to land.
                if (registerPendingVerification && opts.setupIntentId) {
                  try {
                    // Backend re-fetches the SetupIntent from Stripe to
                    // derive the authoritative status, so we only need
                    // the ID. Webhook will keep the row up to date later.
                    await registerPendingVerification(profileId, {
                      setup_intent_id: opts.setupIntentId,
                    });
                  } catch {
                    /* non-fatal — webhook will backfill. */
                  }
                }
                onAttached(null, { pendingVerification: true });
                return;
              }
              onAttached(newPmId);
              onClose();
            }}
            onCancel={onClose}
            settlementCopy={settlementCopy}
          />
        </Elements>
      )}
    </Dialog>
  );
}
